import React, { useRef, useState, useEffect, useCallback } from "react";
import i18n from "@/i18n";

/**
 * Drag-to-compare slider. The AFTER image determines the layout (it sits in
 * normal flow so the wrapper sizes to its intrinsic dimensions). The BEFORE
 * image overlays absolutely with `clip-path: inset(...)` to reveal more or
 * less. This guarantees both images line up regardless of aspect-ratio
 * differences and works inside any parent (inline-block, flex, grid, ...).
 *
 * Props:
 *  - beforeUrl  : string — full URL (or /api/uploads/...) for the original
 *  - afterUrl   : string — full URL for the edited result
 *  - alt        : string — accessibility text
 *  - className  : optional Tailwind classes for the outer wrapper (e.g. max-h)
 *  - testId     : optional data-testid for the wrapper
 */
export default function BeforeAfterSlider({
    beforeUrl,
    afterUrl,
    alt = "Voor/na vergelijking",
    className = "",
    testId = "before-after-slider",
    beforeLabel = null,
    afterLabel = null,
}) {
    const bLabel = beforeLabel ?? i18n.t("common.slider_before");
    const aLabel = afterLabel ?? i18n.t("common.slider_after");
    const wrapRef = useRef(null);
    const [pct, setPct] = useState(50); // percent (0..100) — width of BEFORE shown
    const [dragging, setDragging] = useState(false);
    const [afterLoaded, setAfterLoaded] = useState(false);
    const [beforeLoaded, setBeforeLoaded] = useState(false);
    const bothLoaded = afterLoaded && beforeLoaded;

    // Reset the load state whenever the image URLs change so the loading
    // overlay reappears for a new pair (and never gets stuck "loaded" with
    // stale image bytes from a previous open of the modal).
    useEffect(() => {
        setAfterLoaded(false);
        setBeforeLoaded(false);
    }, [beforeUrl, afterUrl]);

    const updateFromClientX = useCallback((clientX) => {
        const el = wrapRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = clientX - rect.left;
        const next = Math.max(0, Math.min(100, (x / rect.width) * 100));
        setPct(next);
    }, []);

    const onMouseDown = (e) => {
        setDragging(true);
        updateFromClientX(e.clientX);
        e.preventDefault();
    };
    const onTouchStart = (e) => {
        setDragging(true);
        if (e.touches[0]) updateFromClientX(e.touches[0].clientX);
        // Prevent the page from scrolling while the user drags the handle.
        // Required on iOS/Android where touchstart is non-passive by default
        // on the element, but touchmove on `window` is passive — the
        // `passive: false` option in the effect below covers touchmove.
        if (e.cancelable) e.preventDefault();
    };

    useEffect(() => {
        if (!dragging) return;
        const onMove = (e) => {
            const cx = e.touches ? e.touches[0]?.clientX : e.clientX;
            if (cx !== undefined) {
                updateFromClientX(cx);
                // Stop the page scroll while dragging on mobile. Only possible
                // when the listener is registered as non-passive (below).
                if (e.cancelable && e.touches) e.preventDefault();
            }
        };
        const onUp = () => setDragging(false);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        // `passive: false` lets us call preventDefault() inside onMove for
        // touchmove events — without it iOS/Chrome treat the listener as
        // passive and the page keeps scrolling underneath the slider.
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onUp);
        window.addEventListener("touchcancel", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
            window.removeEventListener("touchcancel", onUp);
        };
    }, [dragging, updateFromClientX]);

    return (
        <div
            ref={wrapRef}
            className={`relative inline-block max-w-full overflow-hidden rounded-sm bg-zinc-50 select-none align-top ${className}`}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            data-testid={testId}
            // Hard min-size so the slider is ALWAYS visible — even before the
            // images finish loading, or when the parent layout collapses it.
            style={{ touchAction: "none", cursor: "ew-resize", minWidth: 320, minHeight: 240 }}
        >
            {/* BEFORE image — normal flow, defines the wrapper's intrinsic size.
                We use the ORIGINAL photo as the layout-driver so the slider
                always preserves the broker's framing (landscape stays
                landscape). The `max-h: inherit` makes the BEFORE image
                respect ANY max-h that the parent caller passes via the
                `className` prop (e.g. modals using `max-h-[68vh]`) — so the
                full image stays visible without ever getting cropped at the
                bottom by `overflow-hidden` on the wrapper. */}
            <img
                src={beforeUrl}
                alt={alt + " (voor)"}
                className="block max-w-full min-h-[240px]"
                style={{ maxHeight: "inherit" }}
                draggable={false}
                onLoad={() => setBeforeLoaded(true)}
                onError={() => setBeforeLoaded(true)}
            />
            {/* AFTER image — absolutely positioned on top, clipped by the
                slider handle. Uses `object-cover object-top` so it fills
                exactly the same area as the BEFORE image AND aligns to
                the SAME top edge. This guarantees that when the wrapper's
                `max-h-…` clips the bottom of the BEFORE image (because
                the parent modal limits the slider's height), the AFTER
                image is clipped at the exact same row — so the slider
                position always corresponds to the same scene location on
                both sides. The full uncropped AI result remains
                available via the "AI-resultaat" download button. */}
            <img
                src={afterUrl}
                alt={alt + " (na)"}
                className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
                draggable={false}
                onLoad={() => setAfterLoaded(true)}
                onError={() => setAfterLoaded(true)}
                style={{ clipPath: `inset(0 0 0 ${Math.min(100, pct)}%)` }}
            />

            {/* Loading overlay while either image is still downloading */}
            {!bothLoaded && (
                <div
                    className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center text-white pointer-events-none"
                    data-testid="ba-slider-loading"
                >
                    <div className="h-10 w-10 rounded-full border-2 border-zinc-300 border-t-[#E5A93D] animate-spin mb-3" />
                    <div className="text-sm font-display font-bold">4K beelden laden…</div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                        {afterLoaded ? "✓ Na" : "Na laden…"} · {beforeLoaded ? "✓ Voor" : "Voor laden…"}
                    </div>
                </div>
            )}

            {/* Handle */}
            <div
                className="absolute inset-y-0 z-10 pointer-events-none"
                style={{ left: `calc(${pct}% - 1px)`, width: 2 }}
            >
                <div className="h-full w-0.5 bg-white/95 shadow-[0_0_10px_rgba(0,0,0,0.45)]" />
                <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/95 border border-zinc-300 shadow-lg flex items-center justify-center pointer-events-auto"
                    data-testid="ba-slider-handle"
                >
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-zinc-900" fill="currentColor">
                        <path d="M8 6l-5 6 5 6V6zM16 6v12l5-6-5-6z" />
                    </svg>
                </div>
            </div>

            {/* Labels */}
            <div className="pointer-events-none">
                <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider bg-black/70 text-white px-2 py-1 rounded-sm border border-white/15">
                    {bLabel}
                </span>
                <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-[#E5A93D]/95 text-white font-bold px-2 py-1 rounded-sm">
                    {aLabel}
                </span>
            </div>
        </div>
    );
}
